import IconButton from 'Components/Atoms/IconButton';
import { Call, Headset, Menu } from 'Components/Atoms/Icons';
import Box from 'Components/Atoms/Box';
import React, { useContext } from 'react';
import { useHistory } from 'react-router';
import Typography from 'Components/Atoms/Typography';
import './styles.scss';
import { classHelper } from 'utils/helper';
import { Translations } from 'utils/types';
import { RestaurantContext } from 'Contexts/RestaurantContext';
import useSiaSettings from 'CustomHooks/useSiaSettings';
import useSiaContext from 'CustomHooks/useSiaContext';
import Badge from 'Components/Atoms/Badge';
import { useSupportUnreadCount } from 'CustomHooks/Support/useSupportData';
import Button from 'Components/Atoms/Button';

export type NavBarProps = {
  title?: string;
  right?: React.ReactNode;
  hideMenu?: boolean;
  children?: React.ReactNode;
  hideChildren?: boolean;
  reservationBook?: boolean;
  mainProduct?: {
    label: string;
    labelTranslation?: Translations;
    link: (rId: string, date?: string) => string;
  };
  date?: string;
  className?: string;
  warnOnLeave?: boolean;
  productTranslation?: Translations;
  childrenContainerStyle?: React.CSSProperties;
};

const NavBar = ({
  title = '',
  right,
  children,
  reservationBook = false,
  hideChildren = false,
  mainProduct,
  date,
  className = '',
  warnOnLeave = false,
  hideMenu = false,
  productTranslation,
  childrenContainerStyle,
}: NavBarProps) => {
  const history = useHistory();

  const classNames = classHelper([
    'navbar',
    'space-between',
    'corner',
    reservationBook && 'reservationHeader',
    className,
  ]);

  const {
    restaurantId,
    alert,
    hasSiaV02,
    siaState,
    setSupportOpen,
    setnotificationsV03Open,
  } = useContext(RestaurantContext);

  const toggleSupport = () => {
    setnotificationsV03Open(false);
    setSupportOpen((x) => !x);
  };

  const supportUnread = useSupportUnreadCount();

  const handleClick = (fn: (rId: string, date?: string) => string) => {
    const path = fn(restaurantId || '', date);

    if (warnOnLeave) {
      alert({
        title: 'Are you sure?',
        description: 'You will loose the current progress!',
        onSubmit: () => history?.push(path),
      });
    } else {
      history?.push(path);
    }
  };

  const { hasSia, openSiaModal, unseenCalls } = useSiaContext();

  const siaBadgeCount =
    (siaState.data?.calls_today ?? 0) + (siaState.data?.open_todos ?? 0);

  const badgeCount = hasSiaV02 ? siaBadgeCount : unseenCalls;
  const hasSiaAccess = hasSia || hasSiaV02;

  // console.log("childrenStyle", childrenContainerStyle);

  return (
    <>
      <Box className={classNames} elevation={2} padding="sm" flex>
        <Box flex width="auto">
          <IconButton
            onClick={() => history?.push('/')}
            size={reservationBook ? 'medium' : 'large'}
            disabled={hideMenu}
            style={{ opacity: hideMenu ? 0 : 1 }}
            aria-label="Menu"
          >
            <Menu color="inherit" />
          </IconButton>
          <Box flex style={{ marginLeft: reservationBook ? '1rem' : '0.5rem' }}>
            {!!mainProduct && (
              <>
                <Typography
                  className="breadCrumb"
                  variant="h4"
                  translation={mainProduct.labelTranslation}
                  color="subdued"
                  onClick={() => handleClick(mainProduct.link)}
                >
                  {mainProduct.label}
                </Typography>
                <Typography variant="h4" color="subdued" className="slash">
                  /
                </Typography>
              </>
            )}
            <Typography
              variant="h4"
              translation={productTranslation === undefined ? 'products' : productTranslation}
              style={{ whiteSpace: 'nowrap' }}
            >
              {title}
            </Typography>
          </Box>
        </Box>
        {!childrenContainerStyle ? (
          <Box flex width="auto">
            {!hideChildren && children}
          </Box>
        ) : (
          <Box style={{ ...childrenContainerStyle }}>
            {!hideChildren && children}
          </Box>
        )}

        <Box flex width="auto" className="small-gap">
          {!!restaurantId && (
            <IconButton
              size={reservationBook ? 'small' : 'medium'}
              onClick={toggleSupport}
              aria-label="Support"
              style={{ marginRight: 8 }}
            >
              <Badge number={supportUnread}>
                <Headset color="inherit" />
              </Badge>
            </IconButton>
          )}
          {hasSiaAccess && title !== 'Dashboard' && (
            <IconButton
              size={reservationBook ? 'small' : 'medium'}
              onClick={() => openSiaModal()}
              style={{ marginRight: 8 }}
            >
              <Badge number={badgeCount}>
                <Call />
              </Badge>
            </IconButton>
          )}
          {hasSiaAccess && title === 'Dashboard' && (
            <Button
              style={{ whiteSpace: 'nowrap', marginRight: 8 }}
              endIcon={(p) => <Call {...p} />}
              onClick={() => openSiaModal()}
              variant="secondary"
            >
              Telefonassistent {!!badgeCount ? `(${badgeCount})` : ''}
            </Button>
          )}
          {!hideChildren && right}
        </Box>
      </Box>
    </>
  );
};

export default NavBar;
